import { useCallback, useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { api, apiError, eur, dateFmt, activeCurrency } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { can } from "@/lib/permissions";
import { useI18n } from "@/i18n";
import { StatusBadge, Th, Td } from "@/components/common";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { ArrowLeft, CheckCircle2, Truck, XCircle, CreditCard, Clock, FileText, PackageCheck, Pencil, ChevronLeft, ChevronRight } from "lucide-react";
import { SearchSelect } from "@/components/SearchSelect";
import { AssignCard, StrapCard, isLineOpen, hasMesseLine } from "@/components/orders/OrderAssignCards";
import ShippingCostCard from "@/components/orders/ShippingCostCard";
import { OrderLinesTable, OrderTimeline } from "@/components/orders/OrderParts";
import { PriceProvenance } from "@/components/orders/PriceProvenance";
import { OrderCorrection } from "@/components/orders/OrderCorrection";
import { TaxContextPanel } from "@/components/orders/TaxCaseParts";

const trackingUrl = (carriers, carrier, id) => {
  if (!id || !carrier) return null;
  const c = (carriers || []).find((x) => x.name === carrier);
  return c?.tracking_url_template
    ? c.tracking_url_template.replace("{tracking}", encodeURIComponent(id))
    : null;
};

export default function OrderDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();
  const [o, setO] = useState(null);
  const [override, setOverride] = useState(null); // shortages dialog
  const [reason, setReason] = useState("");
  const [shipQ, setShipQ] = useState(null); // ship dialog {index: qty}
  const [priceDlg, setPriceDlg] = useState(null);
  const [stornoOpen, setStornoOpen] = useState(false);
  const [stornoReason, setStornoReason] = useState("");
  const [shipMeta, setShipMeta] = useState({ carrier: "", tracking_id: "" });
  const [invBusy, setInvBusy] = useState(false);
  const [strapOpts, setStrapOpts] = useState([]);
  const [strapSel, setStrapSel] = useState({});
  const [showAllStrap, setShowAllStrap] = useState(false);
  const [recip, setRecip] = useState(null);
  const [catalog, setCatalog] = useState({ variants: [], bundles: [], messeBundles: [] });
  const [assignSel, setAssignSel] = useState({});
  const [assignQual, setAssignQual] = useState({});
  const [carriers, setCarriers] = useState([]);
  const [orderSeq, setOrderSeq] = useState([]);

  const canSell = can(user, "orders:edit");
  const canShip = can(user, "orders:ship");

  const load = useCallback(() => api.get(`/orders/${id}`).then((r) => setO(r.data)).catch(() => toast.error(t("od.notFound"))), [id, t]);
  useEffect(() => { load(); api.get("/strap-options").then((r) => setStrapOpts(r.data)).catch(() => {}); api.get("/carriers").then((r) => setCarriers(r.data)).catch(() => {}); }, [load]);
  useEffect(() => { api.get("/orders").then((r) => setOrderSeq([...r.data].sort((a, b) => String(b.ordered_at || "").localeCompare(String(a.ordered_at || ""))).map((x) => x.id))).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/variants").then((r) => setCatalog((c) => ({ ...c, variants: [...r.data].sort((a, b) => (a.product_name || "").localeCompare(b.product_name || "", "de")) }))).catch(() => {});
    api.get("/bundles").then((r) => setCatalog((c) => ({ ...c, bundles: [...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")) }))).catch(() => {});
    api.get("/messe-bundles").then((r) => setCatalog((c) => ({ ...c, messeBundles: [...r.data].sort((a, b) => (a.name || "").localeCompare(b.name || "", "de")) }))).catch(() => {});
  }, []);
  const assignOptions = [
    ...catalog.variants.map((v) => ({ value: `v:${v.id}`, label: `${v.product_name} · ${v.sku}` })),
    ...catalog.bundles.map((b) => ({ value: `b:${b.id}`, label: `${b.name} · ${b.sku} (${t("bundle.title")})` })),
    ...catalog.messeBundles.map((b) => ({ value: `m:${b.id}`, label: `${b.name} · ${b.sku} (${t("common.messeBundle")})` })),
  ];
  const assignLine = async (idx) => {
    const l0 = o.lines[idx] || {};
    const raw = assignSel[idx] || (l0.suggested_messe_bundle_id && `m:${l0.suggested_messe_bundle_id}`) || (l0.suggested_bundle_id && `b:${l0.suggested_bundle_id}`) || (l0.suggested_variant_id && `v:${l0.suggested_variant_id}`) || "";
    if (!raw) { toast.error(t("od.chooseProdErr")); return; }
    const kind = raw.slice(0, 1); const ref = raw.slice(2);
    const body = kind === "m" ? { messe_bundle_id: ref } : kind === "b" ? { bundle_id: ref } : { variant_id: ref };
    body.quality = assignQual[idx] || "good";
    try {
      await api.post(`/orders/${id}/lines/${idx}/assign`, body);
      toast.success(t("od.assigned"));
      setAssignSel((s) => { const n = { ...s }; delete n[idx]; return n; }); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const act = async (path, body) => {
    try { await api.post(`/orders/${id}/${path}`, body || {}); toast.success(t("od.actionDone")); load(); }
    catch (e) {
      const d = e?.response?.data?.detail;
      if (path === "confirm" && d && typeof d === "object" && d.shortages) { setOverride(d); return; }
      toast.error(apiError(e));
    }
  };

  const confirmOverride = async () => {
    try { await api.post(`/orders/${id}/confirm`, { override: true, reason }); toast.success(t("od.overrideDone")); setOverride(null); setReason(""); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const openShip = () => { setShipMeta({ carrier: o.carrier || carriers.find((c) => c.is_default)?.name || "", tracking_id: o.tracking_id || "" }); setShipQ(Object.fromEntries(o.lines.map((l, i) => [i, (l.quantity - (l.shipped_qty || 0))]))); };
  const doShip = async () => {
    try {
      const lines = Object.entries(shipQ).map(([index, quantity]) => ({ index: Number(index), quantity: Number(quantity) || 0 })).filter((x) => x.quantity > 0);
      if (!lines.length) { toast.error(t("od.minQty")); return; }
      await api.post(`/orders/${id}/ship`, { lines, carrier: shipMeta.carrier, tracking_id: shipMeta.tracking_id });
      toast.success(t("od.shipBooked")); setShipQ(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };
  const createInvoice = async () => {
    setInvBusy(true);
    try { const r = await api.post(`/orders/${id}/invoice`); toast.success(t("od.invoiceCreated", { number: r.data.invoice_number })); nav(`/rechnung/${r.data.id}`); }
    catch (e) { toast.error(apiError(e)); } finally { setInvBusy(false); }
  };
  const createDN = async () => {
    try { const r = await api.post(`/orders/${id}/delivery-note`); toast.success(`${t("od.deliveryNote")} ${r.data.delivery_note_number}`); nav(`/lieferschein/${r.data.id}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const saveRecip = async () => {
    try { await api.put(`/orders/${id}/recipient`, { contact: recip.contact, shipping_address: recip.shipping_address }); toast.success(t("od.recipSaved")); setRecip(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const createCN23 = async () => {
    try { const r = await api.post(`/orders/${id}/cn23`); toast.success(`CN23 ${r.data.cn23_number}`); nav(`/cn23/${r.data.id}`); }
    catch (e) { toast.error(apiError(e)); }
  };
  const doStorno = async () => {
    try {
      await api.post(`/orders/${id}/cancel`, { reason: stornoReason });
      toast.success(t("od.cancelled"));
      setStornoOpen(false); setStornoReason(""); load();
      api.get("/strap-options").then((r) => setStrapOpts(r.data)).catch(() => {});
    } catch (e) { toast.error(apiError(e)); }
  };
  const setDlgPrice = (key, value) => {
    const rate = (Number(priceDlg.tax_rate) || 0) / 100;
    const next = { ...priceDlg, [key]: value };
    if (key === "net") next.gross = value !== "" && !isNaN(Number(value)) ? String(Number((Number(value) * (1 + rate)).toFixed(2))) : "";
    else if (key === "gross") next.net = value !== "" && !isNaN(Number(value)) ? String(Number((Number(value) / (1 + rate)).toFixed(2))) : "";
    setPriceDlg(next);
  };
  const savePriceCorr = async () => {
    if (!priceDlg?.reason?.trim()) { toast.error(t("od.reasonErr")); return; }
    if (priceDlg.gross === "" || priceDlg.gross === null || isNaN(Number(priceDlg.gross)) || Number(priceDlg.gross) <= 0) { toast.error(t("od.grossErr")); return; }
    try { await api.post(`/orders/${id}/lines/${priceDlg.index}/price`, { unit_price_gross: Number(priceDlg.gross), quantity: Number(priceDlg.quantity) || 0, discount: Number(priceDlg.discount) || 0, reason: priceDlg.reason });
      toast.success(t("od.corrected")); setPriceDlg(null); load(); }
    catch (e) { toast.error(apiError(e)); }
  };
  const assignStrap = async (idx) => {
    const sel = strapSel[idx] || {};
    const strap_variant_id = sel.variant || o.lines[idx]?.strap_variant_id || o.lines[idx]?.strap_suggested_variant_id;
    const quantity = Number(sel.qty ?? o.lines[idx]?.strap_required_quantity ?? o.lines[idx]?.quantity) || 0;
    if (!strap_variant_id) { toast.error(t("od.chooseColor")); return; }
    try { const r = await api.post(`/orders/${id}/lines/${idx}/strap`, { strap_variant_id, quantity });
      toast.success(r.data?.mode === "stock" ? t("od.strapAssigned") : t("od.strapDocSaved"));
      load(); api.get("/strap-options").then((r) => setStrapOpts(r.data)).catch(() => {}); }
    catch (e) { toast.error(apiError(e)); }
  };

  if (!o) return <div className="text-muted-foreground">{t("common.loading")}</div>;
  const cur = o.currency || "EUR";
  const money = (n) => cur === activeCurrency() ? eur(n) : `${Number(n || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${cur}`;

  const seqIdx = orderSeq.indexOf(id);
  const prevId = seqIdx > 0 ? orderSeq[seqIdx - 1] : null;
  const nextId = seqIdx >= 0 && seqIdx < orderSeq.length - 1 ? orderSeq[seqIdx + 1] : null;

  return (
    <div className="max-w-5xl">
      <div className="flex items-center justify-between gap-2 mb-4">
        <button onClick={() => nav("/auftraege")} className="flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground" data-testid="back-btn"><ArrowLeft className="h-4 w-4" /> {t("od.back")}</button>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" disabled={!prevId} onClick={() => prevId && nav(`/auftraege/${prevId}`)} data-testid="prev-order-btn"><ChevronLeft className="h-4 w-4 mr-1" /> {t("od.prevOrder")}</Button>
          <Button variant="outline" size="sm" disabled={!nextId} onClick={() => nextId && nav(`/auftraege/${nextId}`)} data-testid="next-order-btn">{t("od.nextOrder")} <ChevronRight className="h-4 w-4 ml-1" /></Button>
        </div>
      </div>
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-6">
        <div>
          <h1 className="font-heading text-3xl font-semibold">{o.order_number}</h1>
          <p className="text-sm text-muted-foreground">{o.channel}{o.po_number && ` · PO ${o.po_number}`} · {dateFmt(o.ordered_at)}</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <StatusBadge status={o.order_status} testId="detail-order-status" />
          {o.messe && <span className="rounded-full bg-primary/15 text-primary text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide" title={t("od.messeBadge")} data-testid="messe-badge">{t("od.messeBadge")}</span>}
          {o.no_stock && <span className="rounded-full bg-chart-3/15 text-chart-3 text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide" title={t(o.messe ? "ord.messeNoStockTitle" : "ord.noStockHint")} data-testid="no-stock-badge">{t("od.noStockBadge")}</span>}
          {o.revenue_excluded && <span className="rounded-full bg-chart-3/15 text-chart-3 text-[10px] px-2 py-0.5 font-semibold uppercase tracking-wide" title={t("ord.messeNoRevenueTitle")} data-testid="no-revenue-badge">{t("od.noRevenueBadge")}</span>}
          <StatusBadge status={o.payment_status} /><StatusBadge status={o.fulfillment_status} />
        </div>
      </div>

      <div className="flex gap-2 mb-6 flex-wrap">
        {o.order_status === "entwurf" && canSell && <Button data-testid="confirm-order-btn" disabled={o.lines?.some(isLineOpen)} title={o.lines?.some(isLineOpen) ? t("od.assignFirst") : ""} onClick={() => act("confirm")}><CheckCircle2 className="h-4 w-4 mr-1.5" /> {t("od.confirmReserve")}</Button>}
        {(o.order_status === "bestaetigt" || o.order_status === "teilweise_versandt") && canShip && <Button data-testid="ship-order-btn" onClick={openShip}><Truck className="h-4 w-4 mr-1.5" /> {t("od.recordShipment")}</Button>}
        {o.payment_status !== "bezahlt" && o.order_status !== "storniert" && canSell && <Button variant="outline" data-testid="mark-paid-btn" onClick={() => act("mark-paid")}><CreditCard className="h-4 w-4 mr-1.5" /> {t("od.markPaid")}</Button>}
        {o.order_status !== "storniert" && canSell && <Button variant="outline" className="text-destructive" data-testid="cancel-order-btn" onClick={() => { setStornoReason(""); setStornoOpen(true); }}><XCircle className="h-4 w-4 mr-1.5" /> {t("od.cancelOrder")}</Button>}
        {canSell && !o.invoice && (o.order_status === "versandt" || o.order_status === "teilweise_versandt") && <Button variant="outline" data-testid="create-invoice-btn" disabled={invBusy} onClick={createInvoice}><FileText className="h-4 w-4 mr-1.5" /> {t("od.createInvoice")}</Button>}
        {o.invoice && <Button variant="outline" data-testid="view-invoice-btn" onClick={() => nav(`/rechnung/${o.invoice.id}`)}><FileText className="h-4 w-4 mr-1.5" /> {t("od.invoice")} {o.invoice.invoice_number}</Button>}
        {canShip && !o.delivery_note && ["bestaetigt", "teilweise_versandt", "versandt"].includes(o.order_status) && <Button variant="outline" data-testid="create-dn-btn" onClick={createDN}><FileText className="h-4 w-4 mr-1.5" /> {t("od.deliveryNote")}</Button>}
        {o.delivery_note && <Button variant="outline" data-testid="view-dn-btn" onClick={() => nav(`/lieferschein/${o.delivery_note.id}`)}><FileText className="h-4 w-4 mr-1.5" /> {t("od.deliveryNote")} {o.delivery_note.delivery_note_number}</Button>}
        {canSell && o.channel === "Shopify" && !o.cn23 && <Button variant="outline" data-testid="create-cn23-btn" onClick={createCN23}><FileText className="h-4 w-4 mr-1.5" /> {t("od.cn23Create")}</Button>}
        {o.cn23 && <Button variant="outline" data-testid="view-cn23-btn" onClick={() => nav(`/cn23/${o.cn23.id}`)}><FileText className="h-4 w-4 mr-1.5" /> CN23 {o.cn23.cn23_number}</Button>}
        {user?.platform_role === "superadmin" && <OrderCorrection order={o} onDone={load} />}
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <div className="lg:col-span-2 space-y-4">
          <OrderLinesTable o={o} cur={cur} money={money} canSell={canSell} onEditPrice={setPriceDlg} />

          <PriceProvenance order={o} money={money} canSell={canSell} />

          {o.order_status === "entwurf" && canSell && o.lines?.some(isLineOpen) && (
            <AssignCard order={o} money={money} assignSel={assignSel} setAssignSel={setAssignSel}
              assignOptions={assignOptions} onAssign={assignLine}
              assignQual={assignQual} setAssignQual={setAssignQual} />
          )}

          <ShippingCostCard orderId={o.id} canEdit={can(user, "orders:ship")} reloadKey={o.order_status + (o.updated_at || "")} />

          {o.lines?.length > 0 && (
            <StrapCard order={o} canSell={canSell} showAll={showAllStrap} setShowAll={setShowAllStrap}
              strapSel={strapSel} setStrapSel={setStrapSel} strapOpts={strapOpts} onAssignStrap={assignStrap} />
          )}

          {o.reservations?.length > 0 && (
            <div className="rounded-md border border-border bg-card shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-border font-heading font-medium">{t("od.reservations")}</div>
              <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("od.variant")}</Th><Th right>{t("common.quantity")}</Th><Th>{t("ord.colStatus")}</Th></tr></thead>
                <tbody>{o.reservations.map((r) => <tr key={r.id}><Td mono className="text-xs">{r.sku || r.variant_id.slice(0, 8)}</Td><Td right mono>{r.quantity}</Td><Td>{t(`status.${r.status}`, r.status)}</Td></tr>)}</tbody></table>
            </div>
          )}

          {o.events?.length > 0 && (
            <div className="rounded-md border border-border bg-card shadow-sm overflow-hidden">
              <div className="px-4 py-3 border-b border-border font-heading font-medium">{t("od.financeEvents")}</div>
              <table className="w-full"><thead className="bg-muted/50"><tr><Th>{t("inv.type")}</Th><Th>{t("ord.colChannel")}</Th><Th right>{t("od.amount")}</Th></tr></thead>
                <tbody>{o.events.map((e) => <tr key={e.id}><Td>{({ revenue: t("common.revenue"), cogs: t("fin.cogsLabel"), refund: t("fin.refund") }[e.type] || e.type)}</Td><Td>{e.channel}</Td><Td right mono className={e.amount < 0 ? "text-destructive" : "text-primary"}>{eur(e.amount)}</Td></tr>)}</tbody></table>
            </div>
          )}
        </div>

        <div className="space-y-4">
          <div className="rounded-md border border-border bg-card p-4 shadow-sm">
            <div className="flex items-center justify-between mb-2">
              <div className="font-heading font-medium">{t("od.customerDelivery")}</div>
              {(canSell || canShip) && o.order_status !== "storniert" && (
                <Button size="sm" variant="ghost" onClick={() => setRecip({ contact: o.contact || "", shipping_address: o.shipping_address || "" })} data-testid="edit-recipient-btn"><Pencil className="h-3.5 w-3.5 mr-1" /> {t("common.edit")}</Button>
              )}
            </div>
            <div className="text-sm space-y-1 text-muted-foreground">
              <div>{t("ord.contactPerson")}: <span className="text-foreground">{o.contact || "—"}</span></div>
              {o.po_number && <div>{o.channel === "Shopify" ? `Shopify ${t("ord.orderNo")}` : t("ord.poNumber")}: <span className="text-foreground font-mono">{o.po_number}</span></div>}
              <div>{t("ord.paymentTerms")}: <span className="text-foreground">{o.payment_terms || "—"}</span></div>
              <div className="whitespace-pre-line">{t("od.address")}: <span className="text-foreground">{o.shipping_address || "—"}</span></div>
              {(o.carrier || o.tracking_id) && <div className="pt-1 border-t border-border mt-1">{t("od.carrier")}: <span className="text-foreground">{o.carrier || "—"}</span></div>}
              {o.tracking_id && <div>{t("od.trackingId")}: {trackingUrl(carriers, o.carrier, o.tracking_id)
                ? <a href={trackingUrl(carriers, o.carrier, o.tracking_id)} target="_blank" rel="noreferrer" className="text-primary font-mono underline underline-offset-2 hover:opacity-80" data-testid="tracking-link">{o.tracking_id}</a>
                : <span className="text-foreground font-mono">{o.tracking_id}</span>}</div>}
            </div>
          </div>
          <OrderTimeline timeline={o.timeline} />
          <TaxContextPanel order={o} />
        </div>
      </div>

      <Dialog open={!!shipQ} onOpenChange={(o2) => !o2 && setShipQ(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("od.recordShipment")}</DialogTitle></DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div><Label>{t("od.carrier")}</Label>
              <Select value={shipMeta.carrier} onValueChange={(v) => setShipMeta({ ...shipMeta, carrier: v })}>
                <SelectTrigger className="mt-1.5" data-testid="ship-carrier"><SelectValue placeholder={t("ord.select")} /></SelectTrigger>
                <SelectContent>{carriers.filter((c) => c.active !== false || c.name === shipMeta.carrier).map((c) => <SelectItem key={c.id} value={c.name}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>{t("od.trackingId")}</Label><Input className="mt-1.5" value={shipMeta.tracking_id} onChange={(e) => setShipMeta({ ...shipMeta, tracking_id: e.target.value })} data-testid="ship-tracking"  /></div>
          </div>
          <p className="text-sm text-muted-foreground mt-1">{t("od.shipHint")}</p>
          <div className="space-y-2">
            {shipQ && o.lines.map((l, i) => { const rest = l.quantity - (l.shipped_qty || 0); return rest > 0 ? (
              <div key={i} className="flex items-center gap-2">
                <span className="flex-1 text-sm truncate">{l.name} <span className="text-xs text-muted-foreground">({t("od.openQty")} {rest})</span></span>
                <Input type="number" className="w-24 h-9" value={shipQ[i]} max={rest} min={0}
                  onChange={(e) => setShipQ({ ...shipQ, [i]: Math.min(rest, Math.max(0, Number(e.target.value))) })} data-testid={`ship-qty-${i}`} />
              </div>) : null; })}
          </div>
          <DialogFooter><Button data-testid="confirm-ship-btn" onClick={doShip}>{t("od.shipBook")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!priceDlg} onOpenChange={(o2) => !o2 && setPriceDlg(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("od.priceCorrTitle")}</DialogTitle>
            <DialogDescription>{t("od.priceCorrDesc")}</DialogDescription>
          </DialogHeader>
          {priceDlg && <div className="space-y-3">
            <div className="text-sm text-muted-foreground">{t("ord.colItems")}: <span className="text-foreground">{priceDlg.name}</span>
              {priceDlg.sku
                ? <div className="text-xs font-mono text-muted-foreground/80 mt-0.5" data-testid="price-corr-matched">{t("od.matchedProduct")}: {priceDlg.sku}</div>
                : <div className="text-xs text-chart-3 mt-0.5">{t("od.notAssigned")}</div>}
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div><Label>{t("common.quantity")}</Label><Input type="number" min="1" className="mt-1.5" value={priceDlg.quantity} onChange={(e) => setPriceDlg({ ...priceDlg, quantity: e.target.value })} data-testid="price-corr-qty" /></div>
              <div><Label>{t("od.discountNet")} ({cur})</Label><Input type="number" step="0.01" className="mt-1.5" value={priceDlg.discount} onChange={(e) => setPriceDlg({ ...priceDlg, discount: e.target.value })} data-testid="price-corr-discount" /></div>
              <div><Label>{t("od.unitNet")} ({cur})</Label><Input type="number" step="0.01" className="mt-1.5" value={priceDlg.net} onChange={(e) => setDlgPrice("net", e.target.value)} data-testid="price-corr-net" /></div>
              <div><Label>{t("od.unitGross")} ({cur})</Label><Input type="number" step="0.01" className="mt-1.5" value={priceDlg.gross} onChange={(e) => setDlgPrice("gross", e.target.value)} data-testid="price-corr-gross" /></div>
            </div>
            {(() => { const rate = priceDlg.tax_rate || 0; const g = Number(priceDlg.gross) || 0; const q = Number(priceDlg.quantity) || 0; const disc = Number(priceDlg.discount) || 0; const netU = Number((g / (1 + rate / 100)).toFixed(2)); const grossLine = Number((g * q - disc * (1 + rate / 100)).toFixed(2)); const netLine = Number((netU * q - disc).toFixed(2)); const taxLine = Number((grossLine - netLine).toFixed(2)); return (
              <div className="rounded-md bg-muted/50 p-2.5 text-xs text-muted-foreground" data-testid="price-corr-preview">
                {t("od.unitNetShort")}: <span className="font-mono text-foreground">{money(netU)}</span> · {t("common.taxRate")} {rate}% · {t("op.discount")} <span className="font-mono text-foreground">{money(disc)}</span><br />
                {t("od.lineSum")}: {t("common.net")} <span className="font-mono text-foreground">{money(netLine)}</span> · {t("common.taxRate")} <span className="font-mono text-foreground">{money(taxLine)}</span> · {t("common.gross")} <span className="font-mono text-foreground">{money(grossLine)}</span>
              </div>); })()}
            <div><Label>{t("od.reasonRequired")}</Label><Textarea className="mt-1.5" value={priceDlg.reason} onChange={(e) => setPriceDlg({ ...priceDlg, reason: e.target.value })} data-testid="price-corr-reason"  /></div>
            <p className="text-xs text-muted-foreground">{t("od.priceCorrHint")}</p>
          </div>}
          <DialogFooter><Button data-testid="save-price-corr-btn" onClick={savePriceCorr}>{t("od.saveCorrection")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!recip} onOpenChange={(o2) => !o2 && setRecip(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("od.recipEdit")}</DialogTitle>
            <DialogDescription>{t("od.recipDesc")}</DialogDescription>
          </DialogHeader>
          {recip && (
            <div className="space-y-3">
              <div><Label>{t("ord.contactPerson")}</Label><Input className="mt-1.5" value={recip.contact} onChange={(e) => setRecip({ ...recip, contact: e.target.value })} data-testid="recipient-contact" /></div>
              <div><Label>{t("ord.shippingAddress")}</Label>
                <Textarea className="mt-1.5" rows={5} value={recip.shipping_address} onChange={(e) => setRecip({ ...recip, shipping_address: e.target.value })} data-testid="recipient-address" placeholder={"Straße Hausnr.\nPLZ Ort\nLand"} />
                <p className="text-xs text-muted-foreground mt-1">{t("od.recipTip")}</p>
              </div>
              
            </div>
          )}
          <DialogFooter><Button onClick={saveRecip} data-testid="recipient-save-btn">{t("common.save")}</Button></DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={stornoOpen} onOpenChange={(o2) => !o2 && setStornoOpen(false)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("od.stornoTitle")}</DialogTitle></DialogHeader>
          <div className="rounded-md bg-destructive/10 border border-destructive/30 p-3 text-sm text-destructive">
            {o.order_status === "versandt" || o.order_status === "teilweise_versandt"
              ? t("od.stornoWarnShipped")
              : t("od.stornoWarn")}
          </div>
          <p className="text-sm text-muted-foreground">{t("od.irreversible")}</p>
          <div><Label>{t("od.reasonOptional")}</Label><Textarea className="mt-1.5" value={stornoReason} onChange={(e) => setStornoReason(e.target.value)} data-testid="storno-reason"  /></div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setStornoOpen(false)} data-testid="storno-cancel-btn">{t("common.cancel")}</Button>
            <Button className="bg-destructive text-destructive-foreground hover:bg-destructive/90" onClick={doStorno} data-testid="storno-confirm-btn">{t("od.stornoConfirm")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!override} onOpenChange={(o2) => !o2 && setOverride(null)}>
        <DialogContent>
          <DialogHeader><DialogTitle>{t("od.shortageTitle")}</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">{t("od.shortageHint")}</p>
          <table className="w-full"><thead><tr><Th>SKU</Th><Th right>{t("od.needed")}</Th><Th right>{t("inv.available")}</Th></tr></thead>
            <tbody>{override?.shortages?.map((s, i) => <tr key={i}><Td mono>{s.sku}</Td><Td right mono>{s.needed}</Td><Td right mono className="text-destructive">{s.available}</Td></tr>)}</tbody></table>
          {user?.role === "admin" ? (
            <>
              <div><Label>{t("od.overrideReason")}</Label><Textarea data-testid="override-reason" value={reason} onChange={(e) => setReason(e.target.value)} className="mt-1.5" /></div>
              <DialogFooter><Button data-testid="override-confirm-btn" onClick={confirmOverride} disabled={!reason}>{t("od.overrideConfirm")}</Button></DialogFooter>
            </>
          ) : <p className="text-sm text-destructive">{t("od.adminOnly")}</p>}
        </DialogContent>
      </Dialog>
    </div>
  );
}
